import React from 'react';

import { Pagination, PaginationItem } from '@/components/pagination';

import { ITEMS_PER_PAGE } from '../../../constants';
import { useSearchQuery } from '../../../contexts';
import { useSearch } from '../../../hooks/use-search';

const MAX_PAGE_CAP = 40;

// Pagination config (extracted from `src/components/pagination/pagination.js`)
// If there are 5 pages or less, we show all the page numbers
// Else, we use the following rules:
// - We always show:
//   - the first page (1)
//   - the last page
//   - the previous, current and next page
// - We show a truncated item (…):
//   - after the first page, if the second page is not any of the previous cases
//   - before the last page, if the penultimate page is not any of the previous cases
export const getPaginationConfig = (currentPage: number, lastPage: number): number[] =>
  lastPage <= 5
    ? [...Array(lastPage)].map((_, index) => index + 1)
    : [
        ...(currentPage > 1 ? [1] : []),
        ...(currentPage > 3 ? [-1] : []),
        ...(currentPage - 1 > 1 ? [currentPage - 1] : []),
        currentPage,
        ...(currentPage + 1 < lastPage ? [currentPage + 1] : []),
        ...(currentPage + 2 < lastPage ? [-1] : []),
        ...(currentPage < lastPage ? [lastPage] : []),
      ];

export const NumberPagination: React.FC = () => {
  const {
    queryParams: [{ page: currentPage }, setQueryParams],
  } = useSearchQuery();
  const { data } = useSearch();

  const totalResults = data?.jobSearchByQuery?.total ?? 0;
  const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);
  const lastPage = Math.min(totalPages, MAX_PAGE_CAP);
  const paginationConfig = getPaginationConfig(currentPage, lastPage);

  const handlePageChange = (page: number) => () => {
    setQueryParams({ page });
    window.scrollTo(0, 0);
  };

  return lastPage > 1 ? (
    <Pagination
      hidePrevArrow={currentPage === 1}
      hideNextArrow={currentPage >= lastPage}
      onPrev={handlePageChange(currentPage - 1)}
      onNext={handlePageChange(currentPage + 1)}
    >
      {paginationConfig.map((page, i) =>
        page > 0 ? (
          <PaginationItem
            current={page === currentPage}
            onClick={handlePageChange(page)}
            key={`pagination-item-${page}`}
          >
            {page}
          </PaginationItem>
        ) : (
          <PaginationItem truncated key={`pagination-item-truncated-${i}`} />
        )
      )}
    </Pagination>
  ) : null;
};
